import { FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { AI_EVENTS } from 'modules/ai/constants/aiAnalyticsEvents';
import { AiAgentNavigationState } from 'modules/ai/hooks/useNavigateToAiAgent';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';

import Sidebar from './components/Sidebar';
import EmptyState from './components/EmptyState';
import Conversation from './components/Conversation';
import NoAccessState from './components/NoAccessState';
import SocietyAiAgentHeader from './components/SocietyAiAgentHeader';

import { useSocietyAiAgentPage } from './context/SocietyAiAgentPageContext';

import './SocietyAiAgent.scss';

const SocietyAiAgent: FC = () => {
  const { data } = useSocietyAiAgentPage();
  const location = useLocation();
  const mixpanel = useMixpanel();
  const hasTrackedMountRef = useRef(false);

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const hasAiAccess = actualSociety?.configuration?.aiPlan?.enabled === true;

  useEffect(() => {
    if (hasTrackedMountRef.current || !hasAiAccess) return;
    hasTrackedMountRef.current = true;

    const navigationState = location.state as AiAgentNavigationState | undefined;
    let entrySource = 'direct_url';
    if (navigationState?.handoff) {
      entrySource = 'embed_handoff';
    } else if (navigationState?.entrySource) {
      entrySource = navigationState.entrySource;
    }

    trackAiActivity({
      mixpanel,
      state: store.getState() as State,
      eventName: AI_EVENTS.AGENT_PAGE_VIEWED,
      extras: {
        surface: 'agent_page',
        entrySource,
        hasExistingConversation: data.messages.length > 0,
        isHandoff: navigationState?.handoff === true,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (actualSociety && !hasAiAccess) {
    return (
      <div className="navi">
        <NoAccessState />
      </div>
    );
  }

  const hasActiveChat =
    data.messages.length > 0 || data.isThinking || !!data.streamingText;
  return (
    <div className="navi">
      <div className="navi__header">
        <SocietyAiAgentHeader />
      </div>

      <div className="navi__layout">
        <Sidebar />
        <main className="navi__main">
          {hasActiveChat ? <Conversation /> : <EmptyState />}
        </main>
      </div>
    </div>
  );
};

export default SocietyAiAgent;
